// The toast every opted-in mutation gets (moved out of main.tsx in
// SM-958 so a test can drive the same cache the app runs).
import { MutationCache } from "@tanstack/react-query";

import { isDeferredConfirmation } from "@/lib/deferred";
import { humanizeError } from "@/lib/errorMessages";
import { toast } from "@/lib/toast";

// Type the per-mutation `meta` so a mutation can opt into action toasts by
// declaring the copy to show: `successMessage` toasts a ✓ on success and a
// failure toast on error; the optional `errorMessage` overrides the
// humanized server error. Mutations without either (login, background work)
// keep their own bespoke inline feedback and never toast.
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      successMessage?: string;
      errorMessage?: string;
    };
  }
}

// One place owns action feedback: every opted-in mutation's success/failure
// becomes a toast here, so call sites only declare the copy (via meta) and
// keep their existing onSuccess/onError for cache invalidation + modal
// closing. The MutationCache callbacks run for every mutation; the meta
// gate keeps toasts to the action buttons that asked for them. A failure
// toasts humanizeError, which words a broker console refusal by code,
// rule or status and never relays its English server message (SM-958).
export function createMutationCache(): MutationCache {
  return new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const msg = mutation.meta?.successMessage;
      if (msg) toast.success(msg);
    },
    onError: (error, _vars, _ctx, mutation) => {
      const meta = mutation.meta;
      if (!meta?.successMessage && !meta?.errorMessage) return;
      // A deferred confirmation is not a failure — the action is waiting
      // on the operator, and the thrower has already opened the dialog
      // that asks. Toasting here would say it broke instead.
      if (isDeferredConfirmation(error)) return;
      toast.error(meta.errorMessage ?? humanizeError(error));
    },
  });
}
